import type { RouteObject } from "react-router";
import { AuthCallback } from "@/app/AuthCallback";
import { RootGate } from "@/app/RootGate";
import { ScreenTracker } from "@/app/ScreenTracker";
import { OnboardingLayout } from "@/features/onboarding/OnboardingLayout";
import { DateRoute, PlaceRoute, TimeRoute } from "@/features/onboarding/steps/intake-steps";
import { ConsentRoute, RevealRoute, TalkRoute } from "@/features/onboarding/steps/reveal-steps";
import { CodeRoute, EmailRoute, SignInChoiceRoute } from "@/features/onboarding/steps/sign-in-steps";
import { SavingRoute } from "@/features/onboarding/steps/SavingRoute";
import { WelcomeRoute } from "@/features/onboarding/steps/WelcomeRoute";
import { Shell, SignedIn } from "@/features/shell/Shell";
import { TodayTab } from "@/features/today/TodayTab";
import { FriendsTab } from "@/features/friends/FriendsTab";
import { CompatibilityPage } from "@/features/friends/CompatibilityPage";
import { TalkTab } from "@/features/talk/TalkTab";
import { AskPage } from "@/features/talk/AskPage";
import { LifeTab } from "@/features/life/LifeTab";
import { LifeReadingPage } from "@/features/life/LifeReadingPage";
import { ChartsPage, RemediesPage, TransitsPage, VargaPage } from "@/features/life/FreePages";
import { YouTab } from "@/features/you/YouTab";
import { PlanPage } from "@/features/you/PlanPage";
import { MemoryPage } from "@/features/you/MemoryPage";
import { PublicPagePage } from "@/features/you/PublicPagePage";
import { DeleteAccountPage, LanguagePage, PrivacyPage } from "@/features/you/SmallPages";
import { CallSummaryPage } from "@/features/voice/CallSummaryPage";
import { PlanGate } from "@/features/plan/PlanGate";
import { SubscribePage } from "@/features/plan/SubscribePage";

import { LazyVoiceCallPage } from "@/features/voice/lazy";

/**
 * The route tree. Every screen the phone app has is a URL a person could
 * bookmark; first run lives under /start so a reload lands on the same step,
 * and everything signed-in sits under one guard. Inside it, PlanGate sends a
 * locked account (free minutes spent, no plan) to /subscribe.
 */
export const routes: RouteObject[] = [
  {
    // Every screen reports itself to analytics (and to replay's masking) first.
    element: <ScreenTracker />,
    children: [
      { path: "/", element: <RootGate /> },
      { path: "/welcome", element: <WelcomeRoute /> },
      { path: "/auth/callback", element: <AuthCallback /> },
      {
        path: "/start",
        element: <OnboardingLayout />,
        children: [
          { index: true, element: <DateRoute /> },
          { path: "date", element: <DateRoute /> },
          { path: "time", element: <TimeRoute /> },
          { path: "place", element: <PlaceRoute /> },
          { path: "reveal", element: <RevealRoute /> },
          { path: "consent", element: <ConsentRoute /> },
          { path: "sign-in", element: <SignInChoiceRoute /> },
          { path: "email", element: <EmailRoute /> },
          { path: "code", element: <CodeRoute /> },
          { path: "talk", element: <TalkRoute /> },
          { path: "saving", element: <SavingRoute /> },
        ],
      },
      {
        element: <SignedIn />,
        children: [
          // The paywall stands on its own, with no tabs: nothing behind it is open.
          { path: "/subscribe", element: <SubscribePage /> },
          {
            element: <PlanGate />,
            children: [
              {
                element: <Shell />,
                children: [
                  { path: "/today", element: <TodayTab /> },
                  { path: "/friends", element: <FriendsTab /> },
                  { path: "/talk", element: <TalkTab /> },
                  { path: "/life", element: <LifeTab /> },
                  { path: "/you", element: <YouTab /> },
                ],
              },
              {
                element: <Shell tabsOnPhone={false} />,
                children: [
                  { path: "/friends/:userId", element: <CompatibilityPage /> },
                  { path: "/talk/ask", element: <AskPage /> },
                  { path: "/talk/voice", element: <LazyVoiceCallPage /> },
                  { path: "/talk/summary", element: <CallSummaryPage /> },
                  { path: "/life/charts", element: <ChartsPage /> },
                  { path: "/life/charts/:divisor", element: <VargaPage /> },
                  { path: "/life/transits", element: <TransitsPage /> },
                  { path: "/life/remedies", element: <RemediesPage /> },
                  { path: "/life/:reading", element: <LifeReadingPage /> },
                  { path: "/you/plan", element: <PlanPage /> },
                  { path: "/you/memory", element: <MemoryPage /> },
                  { path: "/you/page", element: <PublicPagePage /> },
                  { path: "/you/language", element: <LanguagePage /> },
                  { path: "/you/privacy", element: <PrivacyPage /> },
                  { path: "/you/delete", element: <DeleteAccountPage /> },
                ],
              },
            ],
          },
        ],
      },
      { path: "*", element: <RootGate /> },
    ],
  },
];
